import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { clearFlowSessionToken, getFlowSessionStatus, loadFlowSessionToken } from '../lib/flowApi'
import { cn, getApiBase, getToken } from '../lib/utils'
import { useGuidedFlow } from '../hooks/useGuidedFlow'
import VoiceRecorder from './VoiceRecorder'
import MarkdownMessage from './MarkdownMessage'
import ThinkingBlock from './ThinkingBlock'
import FlowDeliverablesBadge from './flow/FlowDeliverablesBadge'
import FlowDeliverablesDrawer from './flow/FlowDeliverablesDrawer'

type PublicConfig = {
  logo_url: string | null
  public_agent_id: number | null
  public_agent_pages: string[]
  public_widget_enabled: boolean
  public_agent?: {
    id: number
    name: string
    subtitle?: string | null
    instructions?: string | null
    icon_url?: string | null
  } | null
}

type Agent = {
  id: number
  name: string
  icon_url?: string | null
  icon_preview_url?: string | null
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  workspace_id?: number
  agent_mode?: string
  flow_template_id?: number | null
  usage: { message_count: number; limit?: number | null }
  owner_usage: { message_count: number; limit?: number | null }
}

type Message = { role: 'user' | 'assistant'; text: string; providerUsed?: 'openai' | 'ollama' | 'anthropic' }

const PUBLIC_ROOTS = ['/', '/en', '/fr', '/es', '/pricing', '/integrations', '/login', '/agents', '/subjects', '/contents']

const getPublicApiBase = () => getApiBase().replace(/\/app$/, '')

const toAbsoluteUrl = (value: string | null | undefined, base: string): string | null => {
  if (!value) return null
  if (/^https?:\/\//i.test(value)) return value
  const normalizedBase = base.replace(/\/$/, '')
  const normalizedPath = value.startsWith('/') ? value : `/${value}`
  return `${normalizedBase}${normalizedPath}`
}

const isPublicPath = () => {
  try {
    const path = window.location.pathname
    return PUBLIC_ROOTS.some((root) => path === root || path.startsWith(`${root}/`))
  } catch {
    return false
  }
}

const matchPublicPages = (pages: string[], pathname: string): boolean => {
  if (!pages || !pages.length) return false
  if (pages.includes('*')) return true
  return pages.some((page) => {
    if (!page) return false
    if (page === pathname) return true
    if (page.endsWith('*')) {
      const prefix = page.slice(0, -1)
      return pathname.startsWith(prefix)
    }
    return false
  })
}

interface ChatWidgetProps {
  initialAgentId?: number
  autoOpen?: boolean
  position?: 'bottom-right' | 'bottom-left'
  showAgentIcon?: boolean
  showAgentName?: boolean
  showAgentSubtitle?: boolean
  themeColor?: string
}

export default function ChatWidget({
  initialAgentId,
  autoOpen = false,
  position: _position,
  showAgentIcon: _showIcon,
  showAgentName: _showName,
  showAgentSubtitle: _showSubtitle,
  themeColor: _themeColor,
}: ChatWidgetProps = {}) {
  const [open, setOpen] = useState(autoOpen)
  const [agents, setAgents] = useState<Agent[]>([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [agentId, setAgentId] = useState<number | null>(() => {
    if (initialAgentId) return initialAgentId
    try {
      const stored = localStorage.getItem('chat_selected_agent')
      return stored ? Number(stored) : null
    } catch {
      return null
    }
  })

  const [question, setQuestion] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [sending, setSending] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [thinkingText, setThinkingText] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [conversationId, setConversationId] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem('chat_conversation_id')
      return stored ? Number(stored) : null
    } catch {
      return null
    }
  })
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [defaultModel, setDefaultModel] = useState<string | null>(null)
  const [brandingLogo, setBrandingLogo] = useState<string | null>(() => {
    try { return localStorage.getItem('branding_logo_url') || null } catch { return null }
  })
  const iconUrlsRef = useRef<string[]>([])
  const publicAgentRef = useRef<Agent | null>(null)
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null)
  const [publicMode, setPublicMode] = useState(false)
  const [internalWidgetEnabled, setInternalWidgetEnabled] = useState<boolean>(true)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return !!getToken()
    } catch {
      return false
    }
  })
  const [watermarkEnabled, setWatermarkEnabled] = useState(true)
  const [deliverablesOpen, setDeliverablesOpen] = useState(false)
  const [focusDeliverableId, setFocusDeliverableId] = useState<string | null>(null)

  const {
    flowMeta,
    flowSessionToken,
    applyStreamPayload,
    restoreTokenForConversation,
    hydrateFromStatus,
    resetFlow,
    progressLabel,
    showDeliverablesButton,
    isGuidedActive,
  } = useGuidedFlow()

  const publicApiBase = useMemo(() => getPublicApiBase().replace(/\/$/, ''), [])

  const fetchWatermarkStatus = useCallback(async (workspaceId: number) => {
    try {
      const { data } = await api.get(`/public/workspace/${workspaceId}/watermark`)
      setWatermarkEnabled(data.watermark_enabled ?? true)
    } catch {
      setWatermarkEnabled(true) // Fallback : toujours afficher
    }
  }, [])
  const pathname = useMemo(() => {
    try {
      return window.location.pathname || '/'
    } catch {
      return '/'
    }
  }, [])

  useEffect(() => {
    const syncAuth = () => {
      try {
        setIsAuthenticated(!!getToken())
      } catch {
        setIsAuthenticated(false)
      }
    }

    syncAuth()

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === 'access_token' || event.key === 'refresh_token') {
        syncAuth()
      }
    }
    const handleFocus = () => syncAuth()

    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight
    }
  }, [open, messages, isFullscreen])

  useEffect(() => {
    if (!open) {
      setIsFullscreen(false)
    }
  }, [open])

  // Fetch internal widget config for authenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      setInternalWidgetEnabled(true)
      return
    }

    const fetchInternalWidgetConfig = async () => {
      try {
        const { data } = await api.get('/global-config/internal-widget')
        setInternalWidgetEnabled(data?.internal_widget_enabled ?? true)
      } catch {
        setInternalWidgetEnabled(true)
      }
    }
    fetchInternalWidgetConfig()
  }, [isAuthenticated])

  // Keep default model information synced
  useEffect(() => {
    if (!isAuthenticated) {
      setDefaultModel(null)
      return
    }

    const loadDefault = async () => {
      try {
        const { data } = await api.get('/ollama/default')
        setDefaultModel(data?.model || null)
        if (data?.model) {
          try { localStorage.setItem('ollama_default_model', data.model) } catch {}
        }
      } catch {
        try {
          const local = localStorage.getItem('ollama_default_model')
          if (local) setDefaultModel(local)
        } catch {}
      }
    }
    loadDefault()

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'ollama_default_model') {
        try { setDefaultModel(e.newValue || null) } catch {}
      }
    }
    const onCustom = (evt: Event) => {
      const detail = (evt as CustomEvent<string>).detail
      setDefaultModel(detail || null)
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('ollama-default-changed', onCustom as EventListener)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('ollama-default-changed', onCustom as EventListener)
    }
  }, [isAuthenticated])

  const selectedAgent = useMemo(() => agents.find(a => a.id === agentId) || null, [agents, agentId])
  const isGuidedAgent = selectedAgent?.agent_mode === 'guided'

  useEffect(() => {
    if (selectedAgent?.workspace_id) {
      fetchWatermarkStatus(selectedAgent.workspace_id)
    }
  }, [selectedAgent, fetchWatermarkStatus])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'branding_logo_url') {
        try {
          setBrandingLogo(event.newValue || null)
        } catch {
          setBrandingLogo(null)
        }
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  useEffect(() => {
    // sync immediately with potential async updates dispatched elsewhere
    try {
      setBrandingLogo(localStorage.getItem('branding_logo_url') || null)
    } catch {
      setBrandingLogo(null)
    }
  }, [])

  const ensurePublicConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/global-config/public', { baseURL: publicApiBase })
      const normalized: PublicConfig = {
        logo_url: data?.logo_url ?? null,
        public_agent_id: typeof data?.public_agent_id === 'number' ? data.public_agent_id : null,
        public_agent_pages: Array.isArray(data?.public_agent_pages) ? data.public_agent_pages : [],
        public_widget_enabled: data?.public_widget_enabled ?? true,
        public_agent: data?.public_agent ?? null,
      }

      const absoluteLogo = toAbsoluteUrl(normalized.logo_url, publicApiBase)
      if (absoluteLogo) {
        try { localStorage.setItem('branding_logo_url', absoluteLogo) } catch {}
        setBrandingLogo(absoluteLogo)
      } else {
        try { localStorage.removeItem('branding_logo_url') } catch {}
        setBrandingLogo(null)
      }

      const isPublicPathMatch = matchPublicPages(normalized.public_agent_pages, pathname)
      const hasPublicAgent = !!normalized.public_agent_id && !!normalized.public_agent
      const iconUrl = hasPublicAgent ? toAbsoluteUrl(normalized.public_agent?.icon_url || null, publicApiBase) : null
      const publicAgent: Agent | null = hasPublicAgent && normalized.public_agent
        ? {
            id: normalized.public_agent.id,
            name: normalized.public_agent.name,
            icon_url: iconUrl || undefined,
            icon_preview_url: iconUrl || undefined,
            subtitle: normalized.public_agent.subtitle || null,
            instructions: normalized.public_agent.instructions || null,
            message_limit: null,
            usage: { message_count: 0, limit: null },
            owner_usage: { message_count: 0, limit: null },
          }
        : null

      const previousPublicAgentId = publicAgentRef.current?.id ?? null
      publicAgentRef.current = publicAgent

      setPublicConfig(normalized)
      setPublicMode(!isAuthenticated && isPublicPathMatch && !!publicAgent && normalized.public_widget_enabled)

      if (publicAgent) {
        if (!isAuthenticated && isPublicPathMatch) {
          iconUrlsRef.current.forEach(url => {
            try { URL.revokeObjectURL(url) } catch {}
          })
          iconUrlsRef.current = publicAgent.icon_preview_url ? [publicAgent.icon_preview_url] : []
          setAgents([publicAgent])
          setAgentId(publicAgent.id)
        } else {
          setAgents(prev => {
            const others = prev.filter(agent => agent.id !== publicAgent.id)
            return [...others, publicAgent]
          })
        }
      } else {
        if (previousPublicAgentId != null) {
          setAgents(prev => prev.filter(agent => agent.id !== previousPublicAgentId))
        }
        if (!isAuthenticated && isPublicPathMatch) {
          iconUrlsRef.current.forEach(url => {
            try { URL.revokeObjectURL(url) } catch {}
          })
          iconUrlsRef.current = []
          setAgents([])
          setAgentId(null)
        }
      }
    } catch {
      const previousPublicAgentId = publicAgentRef.current?.id ?? null
      publicAgentRef.current = null
      if (previousPublicAgentId != null) {
        setAgents(prev => prev.filter(agent => agent.id !== previousPublicAgentId))
      }
      setPublicConfig(null)
      setPublicMode(false)
    }
  }, [pathname, publicApiBase, isAuthenticated])

  const reloadAgents = useCallback(async () => {
    setAgentsLoading(true)
    try {
      const { data } = await api.get('/agents')
      const items: Agent[] = Array.isArray(data?.items) ? data.items : []
      const nextBlobUrls: string[] = []
      const enriched: Agent[] = await Promise.all(
        items.map(async (agent) => {
          if (agent.icon_url) {
            try {
              const response = await api.get(agent.icon_url, { responseType: 'blob' })
              const blob: Blob = response.data as Blob
              const objectUrl = URL.createObjectURL(blob)
              nextBlobUrls.push(objectUrl)
              return { ...agent, icon_preview_url: objectUrl }
            } catch {
              return { ...agent, icon_preview_url: null }
            }
          }
          return { ...agent, icon_preview_url: null }
        })
      )

      iconUrlsRef.current.forEach(url => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = nextBlobUrls

      setAgents(() => {
        const publicAgent = publicAgentRef.current
        const deduped = publicAgent ? enriched.filter(agent => agent.id !== publicAgent.id) : enriched
        return publicAgent ? [...deduped, publicAgent] : deduped
      })
      if (enriched.length && agentId == null) {
        const stored = (() => {
          try { return localStorage.getItem('chat_selected_agent') } catch { return null }
        })()
        const initial = stored ? Number(stored) : enriched[0]?.id
        setAgentId(initial ?? null)
      } else if (enriched.length === 0) {
        setAgentId(null)
      } else if (agentId != null && !enriched.some(a => a.id === agentId)) {
        setAgentId(enriched[0]?.id ?? null)
      }
    } catch {
      setAgents([])
      iconUrlsRef.current.forEach(url => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = []
      setAgentId(null)
    } finally {
      setAgentsLoading(false)
    }
  }, [agentId, publicMode])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      await ensurePublicConfig()
      if (cancelled) return
      const onPublicRoute = isPublicPath()
      if (isAuthenticated || !onPublicRoute) {
        reloadAgents()
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [ensurePublicConfig, reloadAgents, isAuthenticated])

  useEffect(() => {
    const handler = () => {
      if (publicMode) return
      reloadAgents()
    }
    window.addEventListener('agents-updated', handler)
    return () => {
      window.removeEventListener('agents-updated', handler)
    }
  }, [reloadAgents, publicMode])

  useEffect(() => {
    return () => {
      iconUrlsRef.current.forEach(url => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = []
    }
  }, [])

  useEffect(() => {
    if (publicMode) return undefined
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail
      setAgentId(detail || null)
      if (detail) {
        try { localStorage.setItem('chat_selected_agent', String(detail)) } catch {}
        setOpen(true)
      }
    }
    window.addEventListener('chat-agent-selected', handler as EventListener)
    return () => window.removeEventListener('chat-agent-selected', handler as EventListener)
  }, [publicMode])

  useEffect(() => {
    if (publicMode) return undefined
    const handler = (event: Event) => {
      const convId = (event as CustomEvent<number>).detail
      if (convId) {
        try { localStorage.setItem('chat_conversation_id', String(convId)) } catch {}
        setConversationId(convId)
        setMessages([])
        setOpen(true)
      }
    }
    window.addEventListener('chat-conversation-selected', handler as EventListener)
    return () => window.removeEventListener('chat-conversation-selected', handler as EventListener)
  }, [publicMode])

  // Charger les messages d'une conversation existante quand le widget s'ouvre
  useEffect(() => {
    if (conversationId) {
      restoreTokenForConversation(conversationId)
    }
  }, [conversationId, restoreTokenForConversation])

  // Restaurer l'état du parcours guidé (badge « Mes livrables ») sans renvoyer de message
  useEffect(() => {
    if (!open || !conversationId || publicMode || !isGuidedAgent) return
    let cancelled = false
    const token = loadFlowSessionToken(conversationId)
    getFlowSessionStatus(conversationId, token)
      .then((status) => {
        if (cancelled || !status?.guided_flow) return
        hydrateFromStatus({
          guided_flow: status.guided_flow,
          flow_session_token: status.flow_session_token,
          conversation_id: conversationId,
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, conversationId, publicMode, isGuidedAgent, hydrateFromStatus])

  useEffect(() => {
    if (!isGuidedAgent) {
      resetFlow()
    }
  }, [agentId, isGuidedAgent, resetFlow])

  useEffect(() => {
    if (!open || !conversationId || publicMode) return
    const token = getToken()
    if (!token) return
    fetch(`${getApiBase()}/conversations/${conversationId}/messages`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data?.messages?.length) return
        const mapped = (data.messages as Array<{ role: string; content: string }>)
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({ role: m.role as 'user' | 'assistant', text: m.content }))
        setMessages(mapped)
      })
      .catch(() => {})
  }, [open, conversationId, publicMode])

  useEffect(() => {
    if (publicMode) return
    if (agentId != null) {
      try { localStorage.setItem('chat_selected_agent', String(agentId)) } catch {}
    }
  }, [agentId, publicMode])

  const send = useCallback(async () => {
    if (!agentId || !question.trim()) return
    const q = question.trim()
    setQuestion('')
    setMessages(ms => [...ms, { role: 'user', text: q }])
    setSending(true)
    
    // ⏱️ TTFT measurement start
    const t0 = performance.now()
    let ttft: number | null = null
    
    try {
      const body: any = {
        question: q,
        top_k: 5,
        with_sources: true,
        agent_id: agentId,
        conversation_id: conversationId  // ← Nouveau : support multi-tour
      }
      
      // Utiliser streaming endpoint
      const token = getToken()
      const resp = await fetch(`${getApiBase()}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      })
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}))
        throw new Error(errorData.detail || `HTTP ${resp.status}`)
      }
      
      const reader = resp.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullAnswer = ''
      let providerUsed: 'openai' | 'ollama' | 'anthropic' | undefined
      let firstTokenReceived = false
      
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''
        
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          
          try {
            const data = JSON.parse(line.slice(6))
            
            if (data.error) {
              throw new Error(data.error)
            }
            
            // Gérer les messages de statut (patience)
            if (data.status_message && !firstTokenReceived) {
              setStatusMessage(data.status_message)
            }
            
            // Gérer les événements thinking (modèles avec <think>)
            if (data.thinking_started) {
              setIsThinking(true)
              setThinkingText('')
              setStatusMessage('Réflexion en cours…')
            }
            if (data.thinking) {
              setThinkingText(prev => prev + data.thinking)
            }
            if (data.thinking_done) {
              setIsThinking(false)
              setStatusMessage(null)
            }
            
            if (data.token) {
              // ⏱️ TTFT measurement: capture first token
              if (!firstTokenReceived) {
                ttft = performance.now() - t0
                console.info(`[Chat] ⏱️ TTFT = ${ttft.toFixed(0)}ms`)
                firstTokenReceived = true
                setStatusMessage(null)  // Masquer le statut dès le premier token
                
                // Ajouter le message assistant seulement au premier token
                setMessages(ms => [...ms, { role: 'assistant', text: data.token, providerUsed: undefined }])
                fullAnswer = data.token
              } else {
                // Tokens suivants : mettre à jour le message existant
                fullAnswer += data.token
                setMessages(ms => {
                  const updated = [...ms]
                  updated[updated.length - 1].text = fullAnswer
                  return updated
                })
              }
            }
            
            if (data.guided_flow || data.flow_session_token) {
              applyStreamPayload(data)
            }

            if (data.done) {
              providerUsed = data.provider_used
              if (providerUsed) {
                try { console.info('[Chat] provider_used =', providerUsed) } catch {}
              }
              
              // Sauvegarder conversation_id pour multi-tour
              if (data.conversation_id) {
                setConversationId(data.conversation_id)
                try {
                  localStorage.setItem('chat_conversation_id', String(data.conversation_id))
                  console.info('[Chat] conversation_id saved:', data.conversation_id)
                } catch {}
              }

              if (data.guided_flow || data.flow_session_token) {
                applyStreamPayload(data)
              }
              
              // Ajouter sources
              if (Array.isArray(data.sources) && data.sources.length) {
                const srcs = '\n\nSources:\n' + data.sources.map((s: any) => `- ${s.title ?? 'media ' + s.media_id}`).join('\n')
                fullAnswer += srcs
                setMessages(ms => {
                  const updated = [...ms]
                  updated[updated.length - 1].text = fullAnswer
                  updated[updated.length - 1].providerUsed = providerUsed
                  return updated
                })
              }
            }
          } catch (parseErr) {
            console.error('[Chat] Parse error:', parseErr)
          }
        }
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message
      let err = detail || 'Erreur requête'
      if (typeof detail === 'object') {
        try {
          err = JSON.stringify(detail)
        } catch {
          err = 'Erreur inconnue'
        }
      }
      setMessages(ms => [...ms, { role: 'assistant', text: `Erreur: ${err}` }])
    } finally {
      setSending(false)
      setStatusMessage(null)
      setIsThinking(false)
      
      // ⏱️ Log final TTFT stats
      if (ttft !== null) {
        const status = ttft < 400 ? '✅' : ttft < 1000 ? '⚠️' : '❌'
        console.info(`[Chat] ${status} TTFT final: ${ttft.toFixed(0)}ms (target: <400ms)`)
      }
    }
  }, [agentId, question, conversationId, applyStreamPayload])

  const agentQuotaLabel = useMemo(() => {
    if (!selectedAgent || publicMode) return null
    const used = selectedAgent.usage.message_count
    const limit = selectedAgent.usage.limit ?? null
    if (limit == null) return `${used} messages (illimité)`
    const remaining = Math.max(limit - used, 0)
    return `${used} / ${limit} messages — reste ${remaining}`
  }, [selectedAgent, publicMode])

  const triggerSubtitle = useMemo(() => {
    if (publicMode) {
      return selectedAgent?.subtitle || 'Agent Totemium public'
    }
    if (agentsLoading) return 'Chargement des agents…'
    if (selectedAgent?.subtitle) return selectedAgent.subtitle
    if (selectedAgent) return agentQuotaLabel ?? 'Disponible pour répondre'
    return 'Configurez votre agent dans Totemium'
  }, [publicMode, agentsLoading, selectedAgent, agentQuotaLabel])

  const triggerInitial = useMemo(() => {
    if (selectedAgent?.name) return selectedAgent.name.charAt(0).toUpperCase()
    return 'TM'
  }, [selectedAgent])

  if (publicMode && !agentId) {
    return null
  }

  // Ne pas afficher le widget en mode déconnecté si public_widget_enabled est false
  if (!isAuthenticated && publicConfig && !publicConfig.public_widget_enabled) {
    return null
  }

  // Ne pas afficher le widget en mode connecté si internal_widget_enabled est false
  if (isAuthenticated && !internalWidgetEnabled) {
    return null
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 md:p-8">
      <div className={cn('flex h-full w-full max-w-2xl flex-col overflow-hidden sm:rounded-2xl sm:h-auto sm:max-h-[90vh] border border-zinc-200 dark:border-zinc-800 shadow-2xl bg-white dark:bg-zinc-900')}>
          <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-3">
            {selectedAgent ? (
              <div className="flex items-center gap-2 min-w-0">
                {selectedAgent.icon_preview_url ? (
                  <img src={selectedAgent.icon_preview_url} alt={selectedAgent.name} className="h-8 w-8 shrink-0 rounded-full object-cover border border-emerald-500/60" />
                ) : (
                  <div className="h-8 w-8 shrink-0 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">
                    {selectedAgent.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{selectedAgent.name}</div>
                  {selectedAgent.subtitle && (
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-300 truncate">{selectedAgent.subtitle}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm font-medium">Chat RAG (MVP)</div>
            )}
            <div className="flex items-center gap-1">
              {(isGuidedActive || isGuidedAgent) && progressLabel && (
                <span className="hidden sm:inline rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-300">
                  {progressLabel}
                </span>
              )}
              {showDeliverablesButton && flowMeta && (
                <FlowDeliverablesBadge
                  done={flowMeta.deliverables_done.length}
                  total={flowMeta.deliverable_total}
                  onClick={() => {
                    setFocusDeliverableId(null)
                    setDeliverablesOpen(true)
                  }}
                />
              )}
              <button
                className="px-3 py-1.5 text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-1"
                onClick={() => {
                  if (conversationId) clearFlowSessionToken(conversationId)
                  resetFlow()
                  setDeliverablesOpen(false)
                  setConversationId(null)
                  setMessages([])
                  try {
                    localStorage.removeItem('chat_conversation_id')
                    console.info('[Chat] Nouvelle conversation démarrée')
                  } catch {}
                }}
                aria-label="Nouvelle conversation"
                title="Démarrer une nouvelle conversation"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Nouvelle</span>
              </button>
              <button
                className="p-2 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
                onClick={() => {
                  setOpen(false)
                  setIsFullscreen(false)
                }}
                aria-label="Fermer"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {!publicMode && (
            <div className="px-3 py-3 space-y-2">
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-zinc-500">Agent</label>
                {agentsLoading ? (
                  <div className="text-xs text-zinc-500">Chargement…</div>
                ) : agents.length === 0 ? (
                  <div className="text-xs text-zinc-500">Créez un agent depuis l’onglet Agents.</div>
                ) : (
                  <select
                    value={agentId ?? ''}
                    onChange={e => setAgentId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full border rounded px-2 py-1 bg-transparent"
                  >
                    <option value="">-- Choisir un agent --</option>
                    {agents.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                )}
                {selectedAgent && (
                  <p className="text-[11px] text-zinc-500">
                    {agentQuotaLabel}
                  </p>
                )}
              </div>
              {selectedAgent?.instructions && (
                <div className="rounded-xl border border-zinc-200/70 bg-white/70 p-3 text-xs text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300">
                  <p className="font-semibold text-zinc-700 dark:text-zinc-200">📋 Brief agent</p>
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    <p className="whitespace-pre-wrap">{selectedAgent.instructions}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={scrollerRef} className="px-3 pb-2 flex-1 overflow-y-auto space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                <div className={`inline-flex items-start gap-2`}>
                  {m.role === 'assistant' && (
                    selectedAgent?.icon_preview_url ? (
                      <img
                        src={selectedAgent.icon_preview_url}
                        alt={selectedAgent.name}
                        className="mt-1 h-6 w-6 shrink-0 rounded-full border border-emerald-500/60 object-cover"
                      />
                    ) : (
                      <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-semibold uppercase text-white">
                        {(selectedAgent?.name || 'AI').charAt(0).toUpperCase()}
                      </div>
                    )
                  )}
                  <div className={`inline-block px-3 py-2 rounded ${m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-zinc-100 dark:bg-zinc-800'}`}><MarkdownMessage content={m.text} className="text-sm leading-relaxed" /></div>
                </div>
              </div>
            ))}
            {isThinking && (
              <div className="text-left">
                <ThinkingBlock content={thinkingText} isActive={true} />
              </div>
            )}
            {sending && !isThinking && (
              <div className="text-left">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-200">
                  <span className="flex h-2 w-2 animate-ping rounded-full bg-emerald-500" aria-hidden />
                  <span className="flex h-2 w-2 animate-pulse rounded-full bg-emerald-400" aria-hidden />
                  <span className="flex h-2 w-2 animate-ping rounded-full bg-emerald-300" aria-hidden />
                  <span className="pl-1">{statusMessage || 'Réponse en cours…'}</span>
                </div>
              </div>
            )}
          </div>
          <div className="p-2 border-t border-zinc-200 dark:border-zinc-800 flex gap-2">
            <input
              className="flex-1 border rounded px-3 py-2 bg-transparent"
              placeholder="Votre question..."
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send() }}
              disabled={!agentId || sending}
            />
            <VoiceRecorder
              onTranscription={(text) => setQuestion(text)}
              disabled={!agentId || sending}
            />
            <button onClick={send} disabled={!agentId || sending || !question.trim()} className="px-3 py-2 rounded bg-emerald-600 text-white disabled:opacity-50">Envoyer</button>
          </div>
          {watermarkEnabled && (
            <div className="border-t border-emerald-100/60 bg-emerald-50/80 px-4 py-3 flex items-center justify-center gap-2 dark:border-emerald-900/50 dark:bg-emerald-900/25">
              <span
                className="inline-flex h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.85)]"
                aria-hidden
              />
              <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-200">
                Propulsé par totemium.com
              </span>
            </div>
          )}
        </div>
      <FlowDeliverablesDrawer
        open={deliverablesOpen}
        onClose={() => {
          setDeliverablesOpen(false)
          setFocusDeliverableId(null)
        }}
        conversationId={conversationId}
        flowSessionToken={flowSessionToken}
        flowMeta={flowMeta}
        flowTemplateId={selectedAgent?.flow_template_id}
        focusDeliverableId={focusDeliverableId}
      />
    </div>
  )
}
